import { FlatfileRecord } from '@flatfile/hooks';
import { DATETIME_DESCRIPTION, FIELD_TYPE, submitAction } from 'components/imports/blueprintUtils';
import {
    MAX_STR_LENGTH,
    base,
    fixAddress,
    nullable,
    required,
} from 'components/imports/schemaUtils';
import { ImportStrategy } from 'components/imports/types';
import { IMPORT_PLANS_LINK } from 'constants/links';
import { isUSA } from 'helpers/address';
import { yup } from 'helpers/yup';
import { ValidationError } from 'yup';

const recurringPlanSchema = yup.object().shape({
    first_name: required.label('First Name').max(MAX_STR_LENGTH),
    last_name: required.label('Last Name').max(MAX_STR_LENGTH),
    amount: required.label('Amount').asNumber(schema => schema.moreThan(0)),
    frequency: required.label('Frequency').oneOf(['monthly', 'quarterly', 'yearly']),
    campaign_code: required.label('Campaign Code').length(6).alphanum(),
    last_bill_date: required.label('Last Bill Date').date({ before: true }),
    fund: nullable.label('Fund Code').max(MAX_STR_LENGTH),
    service_payer_id: required
        .label('Stripe Customer ID')
        .matches(/cus_[a-zA-Z0-9]+/, '${label} must start with "cus_"'),
    service_source_id: required
        .label('Stripe Payment Method ID')
        .matches(
            /(src_|pm_|card_|ba_)[a-zA-Z0-9]+/,
            '${label} must start with either "src_", "pm_", "card_", or "ba_"',
        ),
    email: required.label('Email').email(),
    phone: nullable.label('Phone').max(MAX_STR_LENGTH),
    address_1: required.label('Address Line 1').max(MAX_STR_LENGTH),
    address_2: nullable.label('Address Line 2').max(MAX_STR_LENGTH),
    city: required.label('City').max(MAX_STR_LENGTH),
    state: required.label('State').max(MAX_STR_LENGTH).state(),
    zipcode: base
        .label('Postal Code')
        .max(MAX_STR_LENGTH)
        .when('country', {
            is: (country: string | null) => isUSA(country),
            then: schema => schema.required().postalCode(),
            otherwise: schema => schema.nullable(),
        }),
    country: required.label('Country').max(MAX_STR_LENGTH).country(),
});

const useRecurringPlanImportStrategy = (): ImportStrategy => {
    return {
        displayName: 'recurring plans',
        type: 'transaction_plans',
        hasDownloadTemplate: false,
        helpCenterUrl: IMPORT_PLANS_LINK,
        blueprint: {
            name: 'Import recurring plans',
            sheets: [
                {
                    name: 'Recurring plans',
                    description: 'Migrate recurring plans from another platform.',
                    slug: 'transaction_plans',
                    readonly: false,
                    fields: [
                        {
                            label: 'First Name',
                            key: 'first_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Last Name',
                            key: 'last_name',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Email Address',
                            key: 'email',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Amount ($)',
                            key: 'amount',
                            type: FIELD_TYPE.number,
                        },
                        {
                            label: 'Frequency',
                            key: 'frequency',
                            type: FIELD_TYPE.enum,
                            config: {
                                options: [
                                    { value: 'monthly', label: 'Monthly' },
                                    { value: 'quarterly', label: 'Quarterly' },
                                    { value: 'yearly', label: 'Yearly' },
                                ],
                            },
                        },
                        {
                            label: 'Last Bill Date',
                            key: 'last_bill_date',
                            type: FIELD_TYPE.date,
                            description: DATETIME_DESCRIPTION,
                        },
                        {
                            label: 'Campaign Code',
                            key: 'campaign_code',
                            type: FIELD_TYPE.string,
                            description:
                                "The code that identifies the campaign that should be associated with the plan. Found on a campaign's details page.",
                        },
                        {
                            label: 'Fund',
                            key: 'fund',
                            type: FIELD_TYPE.string,
                            description:
                                'The code that identifies the fund that should be associated with the plan.',
                        },
                        {
                            label: 'Stripe Customer ID',
                            key: 'service_payer_id',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Stripe Payment Method ID',
                            key: 'service_source_id',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Phone Number',
                            key: 'phone',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 1',
                            key: 'address_1',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Address Line 2',
                            key: 'address_2',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'City',
                            key: 'city',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'State',
                            key: 'state',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Postal Code',
                            key: 'zipcode',
                            type: FIELD_TYPE.string,
                        },
                        {
                            label: 'Country',
                            key: 'country',
                            type: FIELD_TYPE.string,
                        },
                    ],
                },
            ],
            actions: [submitAction],
        },
        recordHook: (record: FlatfileRecord) => {
            fixAddress(record);

            try {
                recurringPlanSchema.validateSync(record.value, { abortEarly: false });
            } catch (err) {
                if (!(err instanceof ValidationError)) {
                    return record;
                }

                if (!err.inner) {
                    record.addError(err.path!, err.message);

                    return record;
                }

                err.inner.forEach((e: any) => {
                    record.addError(e.path, e.message);
                });
            }

            return record;
        },
    };
};

export { useRecurringPlanImportStrategy };
